import React, { useLayoutEffect, useMemo, useRef } from "react";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import {
    BaseCellProps,
    useCreationModalPending,
} from "@components/spreadsheet/cells";
import { CounterpartyCombobox } from "./CounterpartyCombobox";
import styles from "./TransactionSplitCounterpartyCell.module.scss";

interface Props extends BaseCellProps<string> {
    counterpartiesMap: Map<string, string>;
}

export const TransactionSplitCounterpartyCell: React.FC<Props> = ({
    value,
    isEditing,
    finishEditing,
    rowIdx,
    colId,
    enterEditModeKey,
    counterpartiesMap,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { isPendingRef, setCreationModalPending } = useCreationModalPending();
    const triggerClassName = useMemo(
        () => `counterparty-cell-trigger-${rowIdx}-${colId}`,
        [rowIdx, colId],
    );

    const resolvedName = value ? counterpartiesMap.get(value) : undefined;

    const selectedCounterparty = useMemo(() => {
        if (!value) {
            return undefined;
        }
        return { id: value, name: resolvedName ?? "" };
    }, [value, resolvedName]);

    useLayoutEffect(() => {
        if (isEditing && containerRef.current) {
            const trigger = containerRef.current.querySelector(
                `.${triggerClassName}`,
            ) as HTMLElement;
            trigger?.click();
        }
    }, [isEditing, triggerClassName]);

    if (isEditing) {
        return (
            <>
                <div
                    ref={containerRef}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                        if (["Enter", "Escape"].includes(e.key)) {
                            finishEditing();
                        }
                    }}
                >
                    <CounterpartyCombobox
                        selectedCounterparty={selectedCounterparty}
                        onChange={(counterparty) =>
                            finishEditing(counterparty?.id ?? null)
                        }
                        onOpenChange={(open) => {
                            if (!open && !isPendingRef.current) {
                                finishEditing();
                            }
                        }}
                        onCreationModalOpenChange={(open) => {
                            setCreationModalPending(open);
                            if (!open) {
                                finishEditing();
                            }
                        }}
                        forTransactionEditing
                        cellEditor={{ openingKey: enterEditModeKey }}
                        hideArrow
                        triggerButtonProps={{
                            variant: "primary",
                            size: "sm",
                            className: triggerClassName,
                            style: {
                                position: "absolute",
                                opacity: 0,
                                pointerEvents: "none",
                            },
                        }}
                    />
                </div>
                <CounterpartyNameDisplay
                    name={resolvedName}
                    counterpartyId={value ?? undefined}
                />
            </>
        );
    }

    return (
        <CounterpartyNameDisplay
            name={resolvedName}
            counterpartyId={value ?? undefined}
        />
    );
};

const CounterpartyNameDisplay: React.FC<{
    name?: string;
    counterpartyId?: string;
}> = ({ name, counterpartyId }) => {
    if (!name) {
        return null;
    }

    return (
        <BreakableText
            text={name}
            showTooltip
            className={styles.counterpartyName}
            copyId={counterpartyId}
            copyLabel="Counterparty"
        />
    );
};
